import { useState, type FormEvent } from "react";
import { Button, DataTable, Input, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { getBranding, updateBranding, uploadFavicon, uploadLogo } from "../../api/brandingApi";
import { createDomain, deleteDomain, listDomains, verifyDomain } from "../../api/domainsApi";
import { getEcommerceSettings, updateEcommerceSettings } from "../../api/ecommerceSettingsApi";
import type { TenantDomainOut, TenantDomainTargetApp } from "../../api/types";
import { CustomFieldsEditor } from "../../components/CustomFieldsEditor";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";
import { API_ROOT_URL, ECOMMERCE_URL } from "../../env";

/** `logo_path`/`favicon_path` are backend-relative paths (`/img/branding/{tenant}/...`,
 * `branding_service.py::logo_path`/`favicon_path`) meant to be served by the API origin,
 * not this app's own — used as a bare `<img src>` they 404 against the dashboard's own
 * origin instead. Same resolution `DocumentGeneratorPage.tsx` already does for its own
 * logo preview. */
function resolveAssetUrl(path: string | null): string | null {
  if (!path) return null;
  return path.startsWith("http") ? path : `${API_ROOT_URL}${path}`;
}

const DOMAIN_COLUMNS: DataTableColumn<TenantDomainOut, keyof TenantDomainOut>[] = [
  { key: "domain", header: "Domain", render: (r) => r.domain },
  { key: "target_app", header: "App", render: (r) => (r.target_app === "DASHBOARD_ADMIN" ? "Dashboard" : "Online Store") },
  {
    key: "verified",
    header: "Ownership",
    render: (r) => <span className={`badge ${r.verified ? "badge--current" : "badge--due-soon"}`}>{r.verified ? "Verified" : "Not verified"}</span>,
  },
  {
    key: "tls_status",
    header: "TLS",
    render: () => (
      <span title="Live request routing and certificate issuance aren't wired up in this deployment yet — verifying a domain here only proves you control its DNS.">
        Not live
      </span>
    ),
  },
];

/**
 * §11 — branding (colors, logo, favicon) + custom domains. OWNER-only for writes; the
 * `GET /branding` read this replaces `MOCK_TENANT_BRANDING` with is open to every role
 * (see `NavHeader.tsx`). Domain verification here proves DNS ownership only — it does not
 * make any traffic route to a custom domain in this deployment (`backend/app/services/
 * domain_service.py`'s docstring has the full honest scope boundary).
 */
export function BrandingSettingsPage() {
  const { user, ensureFreshToken } = useAuth();
  const isOwner = user?.role === "OWNER";

  const brandingState = useAsyncData(() => ensureFreshToken().then((token) => getBranding(token)), []);
  const domainsState = useAsyncData(() => ensureFreshToken().then((token) => listDomains(token)), []);
  const ecommerceState = useAsyncData(() => ensureFreshToken().then((token) => getEcommerceSettings(token)), []);
  const [ecommerceSaving, setEcommerceSaving] = useState(false);
  const [ecommerceError, setEcommerceError] = useState<string | null>(null);

  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [formInitialized, setFormInitialized] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  // Business details — reused (not retyped) everywhere the Documents page shows the
  // seller/issuer block, e.g. `DocumentGeneratorPage.tsx`.
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [tin, setTin] = useState("");
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [businessSaving, setBusinessSaving] = useState(false);
  const [businessError, setBusinessError] = useState<string | null>(null);
  const [businessSuccess, setBusinessSuccess] = useState<string | null>(null);

  const [newDomain, setNewDomain] = useState("");
  const [newDomainTarget, setNewDomainTarget] = useState<TenantDomainTargetApp>("DASHBOARD_ADMIN");
  const [domainError, setDomainError] = useState<string | null>(null);
  const [domainBusyId, setDomainBusyId] = useState<string | null>(null);
  const [pendingDeleteDomain, setPendingDeleteDomain] = useState<TenantDomainOut | null>(null);

  const [linkCopied, setLinkCopied] = useState(false);

  if (brandingState.data && !formInitialized) {
    setPrimaryColor(brandingState.data.primary_color ?? "");
    setSecondaryColor(brandingState.data.secondary_color ?? "");
    setAccentColor(brandingState.data.accent_color ?? "");
    setDisplayName(brandingState.data.display_name ?? "");
    setAddress(brandingState.data.address ?? "");
    setPhone(brandingState.data.phone ?? "");
    setTin(brandingState.data.tin ?? "");
    setCustomFields(brandingState.data.custom_fields ?? {});
    setFormInitialized(true);
  }

  if (!isOwner) {
    return (
      <div className="page">
        <div className="page__header">
          <div>
            <h1 className="page__title">Branding & Domains</h1>
          </div>
        </div>
        <StatusBanner kind="info">Branding management is OWNER-only. Your role ({user?.role}) doesn't have access.</StatusBanner>
      </div>
    );
  }

  async function handleSaveColors(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    setSaving(true);
    try {
      const token = await ensureFreshToken();
      await updateBranding(token, {
        primary_color: primaryColor || null,
        secondary_color: secondaryColor || null,
        accent_color: accentColor || null,
        display_name: displayName || null,
      });
      setFormSuccess("Branding saved.");
      brandingState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError || error instanceof Error ? error.message : "Could not save branding.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveBusinessDetails(event: FormEvent) {
    event.preventDefault();
    setBusinessError(null);
    setBusinessSuccess(null);
    setBusinessSaving(true);
    try {
      const token = await ensureFreshToken();
      await updateBranding(token, {
        address: address || null,
        phone: phone || null,
        tin: tin || null,
        custom_fields: customFields,
      });
      setBusinessSuccess("Business details saved.");
      brandingState.reload();
    } catch (error) {
      setBusinessError(error instanceof ApiError || error instanceof Error ? error.message : "Could not save business details.");
    } finally {
      setBusinessSaving(false);
    }
  }

  async function handleUpload(kind: "logo" | "favicon", file: File | undefined) {
    if (!file) return;
    setFormError(null);
    setFormSuccess(null);
    try {
      const token = await ensureFreshToken();
      if (kind === "logo") await uploadLogo(token, file);
      else await uploadFavicon(token, file);
      setFormSuccess(kind === "logo" ? "Logo uploaded." : "Favicon uploaded.");
      brandingState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError || error instanceof Error ? error.message : `Could not upload ${kind}.`);
    }
  }

  async function handleCreateDomain(event: FormEvent) {
    event.preventDefault();
    setDomainError(null);
    if (!newDomain.trim()) return;
    try {
      const token = await ensureFreshToken();
      await createDomain(token, { domain: newDomain.trim(), target_app: newDomainTarget });
      setNewDomain("");
      domainsState.reload();
    } catch (error) {
      setDomainError(error instanceof ApiError || error instanceof Error ? error.message : "Could not add domain.");
    }
  }

  async function handleVerify(domainId: string) {
    setDomainBusyId(domainId);
    setDomainError(null);
    try {
      const token = await ensureFreshToken();
      const result = await verifyDomain(token, domainId);
      if (!result.verified) {
        setDomainError("DNS record not found yet (or hasn't propagated). Try again in a few minutes.");
      }
      domainsState.reload();
    } catch (error) {
      setDomainError(error instanceof ApiError || error instanceof Error ? error.message : "Could not verify domain.");
    } finally {
      setDomainBusyId(null);
    }
  }

  async function handleConfirmDeleteDomain() {
    if (!pendingDeleteDomain) return;
    const domainId = pendingDeleteDomain.id;
    setDomainBusyId(domainId);
    try {
      const token = await ensureFreshToken();
      await deleteDomain(token, domainId);
      setPendingDeleteDomain(null);
      domainsState.reload();
    } catch (error) {
      setDomainError(error instanceof ApiError || error instanceof Error ? error.message : "Could not remove domain.");
    } finally {
      setDomainBusyId(null);
    }
  }

  const branding = brandingState.data;
  const storefrontUrl = branding ? `${ECOMMERCE_URL}/${branding.slug}` : null;
  const ecommerceEnabled = ecommerceState.data?.ecommerce_enabled ?? null;

  async function handleCopyStorefrontLink() {
    if (!storefrontUrl) return;
    await navigator.clipboard.writeText(storefrontUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  }

  async function handleToggleEcommerce(nextEnabled: boolean) {
    setEcommerceError(null);
    setEcommerceSaving(true);
    try {
      const token = await ensureFreshToken();
      await updateEcommerceSettings(token, { ecommerce_enabled: nextEnabled });
      ecommerceState.reload();
    } catch (error) {
      setEcommerceError(error instanceof ApiError || error instanceof Error ? error.message : "Could not update storefront status.");
    } finally {
      setEcommerceSaving(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Branding & Domains</h1>
          <p className="muted">Logo, colors, and optional custom domains for your dashboard and storefront.</p>
        </div>
      </div>

      {brandingState.loading ? <p>Loading…</p> : null}
      {brandingState.error ? <StatusBanner kind="error">{brandingState.error}</StatusBanner> : null}

      {storefrontUrl ? (
        <section className="card" style={{ marginBottom: 24 }}>
          <h2>Online storefront</h2>
          {ecommerceState.error ? <StatusBanner kind="error">{ecommerceState.error}</StatusBanner> : null}
          {ecommerceError ? <StatusBanner kind="error">{ecommerceError}</StatusBanner> : null}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span className={`badge ${ecommerceEnabled ? "badge--current" : "badge--overdue"}`}>
              {ecommerceEnabled === null ? "Loading…" : ecommerceEnabled ? "Online" : "Offline"}
            </span>
            <Button
              type="button"
              variant="secondary"
              disabled={ecommerceEnabled === null || ecommerceSaving}
              onClick={() => void handleToggleEcommerce(!ecommerceEnabled)}
            >
              {ecommerceSaving ? "Saving…" : ecommerceEnabled ? "Take storefront offline" : "Bring storefront online"}
            </Button>
          </div>
          {ecommerceEnabled ? (
            <>
              <p className="muted">
                Share this link with your customers — it always points to your online store, no matter what changes
                here.
              </p>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <code style={{ padding: "6px 10px", background: "var(--sl-surface-muted, #f1f5f9)", borderRadius: 6 }}>{storefrontUrl}</code>
                <Button type="button" variant="ghost" onClick={() => void handleCopyStorefrontLink()}>
                  {linkCopied ? "Copied!" : "Copy link"}
                </Button>
              </div>
            </>
          ) : ecommerceEnabled === false ? (
            <p className="muted">
              Your storefront is currently offline — <code>{storefrontUrl}</code> shows customers a not-found page
              until you bring it online. Products stay hidden from checkout while offline, even ones marked
              "available online".
            </p>
          ) : null}
        </section>
      ) : null}

      <section className="card" style={{ marginBottom: 24 }}>
        <h2>Colors & display name</h2>
        <form onSubmit={handleSaveColors} className="form-grid">
          <Input label="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} hint="Shown instead of the legal business name where applicable" />
          <Input label="Primary color" type="color" value={primaryColor || "#1a73e8"} onChange={(e) => setPrimaryColor(e.target.value)} />
          <Input label="Secondary color" type="color" value={secondaryColor || "#334155"} onChange={(e) => setSecondaryColor(e.target.value)} />
          <Input label="Accent color" type="color" value={accentColor || "#f97316"} onChange={(e) => setAccentColor(e.target.value)} />
          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          {formSuccess ? <StatusBanner kind="info">{formSuccess}</StatusBanner> : null}
          <Button type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </form>

        <div style={{ marginTop: 16, display: "flex", gap: 24 }}>
          <div>
            <label>Logo</label>
            <div>
              {branding?.logo_path ? <img src={resolveAssetUrl(branding.logo_path) ?? undefined} alt="Logo" style={{ height: 48 }} /> : <span className="muted">No logo uploaded</span>}
            </div>
            <input type="file" accept="image/*" onChange={(e) => void handleUpload("logo", e.target.files?.[0])} />
          </div>
          <div>
            <label>Favicon</label>
            <div>
              {branding?.favicon_path ? <img src={resolveAssetUrl(branding.favicon_path) ?? undefined} alt="Favicon" style={{ height: 32 }} /> : <span className="muted">No favicon uploaded</span>}
            </div>
            <input type="file" accept="image/*" onChange={(e) => void handleUpload("favicon", e.target.files?.[0])} />
          </div>
        </div>
      </section>

      <section className="card" style={{ marginBottom: 24 }}>
        <h2>Business details</h2>
        <p className="muted">
          Address, phone, and TIN are reused automatically on every document you create (Documents page) —
          set them here once instead of retyping them each time.
        </p>
        <form onSubmit={handleSaveBusinessDetails} className="form-grid">
          <Input label="Address / location" value={address} onChange={(e) => setAddress(e.target.value)} />
          <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <Input label="Tax Identification Number (TIN)" value={tin} onChange={(e) => setTin(e.target.value)} />
          <CustomFieldsEditor value={customFields} onChange={setCustomFields} />
          {businessError ? <StatusBanner kind="error">{businessError}</StatusBanner> : null}
          {businessSuccess ? <StatusBanner kind="info">{businessSuccess}</StatusBanner> : null}
          <Button type="submit" disabled={businessSaving}>
            {businessSaving ? "Saving…" : "Save"}
          </Button>
        </form>
      </section>

      <section className="card">
        <h2>Custom domains</h2>
        <p className="muted">
          Verifying a domain here confirms you control its DNS. Live traffic routing and TLS for custom domains
          isn't wired up in this deployment yet — your default subdomain keeps working regardless.
        </p>

        <form onSubmit={handleCreateDomain} className="form-grid" style={{ marginBottom: 16 }}>
          <Input label="Domain" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="shop.example.com" />
          <div className="ml-field-select">
            <label htmlFor="target-app">App</label>
            <select id="target-app" value={newDomainTarget} onChange={(e) => setNewDomainTarget(e.target.value as TenantDomainTargetApp)}>
              <option value="DASHBOARD_ADMIN">Dashboard</option>
              <option value="ECOMMERCE_STORE">Online Store</option>
            </select>
          </div>
          {domainError ? <StatusBanner kind="error">{domainError}</StatusBanner> : null}
          <Button type="submit">Add domain</Button>
        </form>

        {domainsState.loading ? <p>Loading…</p> : null}
        {domainsState.error ? <StatusBanner kind="error">{domainsState.error}</StatusBanner> : null}
        {domainsState.data ? (
          <>
            <DataTable columns={DOMAIN_COLUMNS} rows={domainsState.data} getRowId={(r) => r.id} />
            {domainsState.data.map((d) =>
              !d.verified ? (
                <div key={`${d.id}-instructions`} className="muted" style={{ marginTop: 8 }}>
                  Add a DNS TXT record named <code>{d.verification_dns_record}</code> with value{" "}
                  <code>{d.verification_token}</code>, then verify.
                  <Button type="button" variant="ghost" disabled={domainBusyId === d.id} onClick={() => void handleVerify(d.id)}>
                    {domainBusyId === d.id ? "Checking…" : "Verify"}
                  </Button>
                  <Button type="button" variant="ghost" disabled={domainBusyId === d.id} onClick={() => setPendingDeleteDomain(d)}>
                    Remove
                  </Button>
                </div>
              ) : (
                <div key={`${d.id}-actions`} style={{ marginTop: 8 }}>
                  <Button type="button" variant="ghost" disabled={domainBusyId === d.id} onClick={() => setPendingDeleteDomain(d)}>
                    Remove
                  </Button>
                </div>
              ),
            )}
          </>
        ) : null}

        {pendingDeleteDomain ? (
          <div className="panel" style={{ marginTop: 12 }}>
            <h2 className="panel__title">Remove {pendingDeleteDomain.domain}?</h2>
            <p className="muted">
              This immediately stops routing that domain to your {pendingDeleteDomain.target_app === "DASHBOARD_ADMIN" ? "dashboard" : "online store"}.
              You'll need to re-verify DNS ownership if you add it again later.
            </p>
            <div className="form-actions">
              <Button variant="danger" type="button" disabled={domainBusyId === pendingDeleteDomain.id} onClick={() => void handleConfirmDeleteDomain()}>
                {domainBusyId === pendingDeleteDomain.id ? "Removing…" : "Remove domain"}
              </Button>
              <Button variant="ghost" type="button" onClick={() => setPendingDeleteDomain(null)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
